// Comprehensive massage services data for BookaTherapy
// Organized by categories with most common services prioritized

export const MOST_COMMON_SERVICES = [
  "Swedish Massage",
  "Deep Tissue Massage",
  "Sports Massage",
  "Hot Stone Massage",
  "Prenatal/Pregnancy Massage",
  "Trigger Point Therapy",
  "Lymphatic Drainage Massage",
  "Aromatherapy Massage",
  "Myofascial Release",
  "Reflexology",
  "Chair Massage",
  "Thai Massage",
  "Shiatsu",
  "Cupping Therapy",
  "Craniosacral Therapy",
  "Oncology Massage"
];

export const massageCategories = {
  clinical_therapeutic_massage: {
    name: "Clinical & Therapeutic Massage",
    description: "Evidence-based therapeutic massage techniques performed by RMTs for pain relief, injury recovery, and musculoskeletal conditions",
    types: [
      {
        name: "Swedish Massage",
        description: "Most common type using long strokes, kneading, and light tapping for relaxation and circulation",
        isCommon: true
      },
      {
        name: "Deep Tissue Massage",
        description: "Targets deeper muscle layers using firm pressure to address chronic tension and knots",
        isCommon: true
      },
      {
        name: "Sports Massage",
        description: "Designed for athletes to enhance performance, prevent injuries, and aid recovery",
        isCommon: true
      },
      {
        name: "Trigger Point Therapy",
        description: "Releases nodular trigger points in taut muscle bands to relieve pain",
        isCommon: true
      },
      {
        name: "Myofascial Release",
        description: "Releases tension in the fascia (connective tissue) surrounding muscles",
        isCommon: true
      },
      {
        name: "Neuromuscular Therapy",
        description: "Addresses imbalances in the nervous system and muscular structure",
        isCommon: false
      }
    ]
  },
  specialized_therapeutic_techniques: {
    name: "Specialized Therapeutic Techniques",
    description: "Advanced clinical massage techniques for specific medical conditions and therapeutic goals",
    types: [
      {
        name: "Lymphatic Drainage Massage",
        description: "Stimulates lymph fluid movement to reduce swelling and boost immune function",
        isCommon: true
      },
      {
        name: "Prenatal/Pregnancy Massage",
        description: "Adapted techniques safe for pregnant women to reduce discomfort",
        isCommon: true
      },
      {
        name: "Oncology Massage",
        description: "Gentle massage for cancer patients undergoing treatment",
        isCommon: true
      },
      {
        name: "Hot Stone Massage",
        description: "Uses heated basalt stones for deep relaxation and muscle tension relief",
        isCommon: true
      },
      {
        name: "Craniosacral Therapy",
        description: "Gentle manipulation of craniosacral system (skull, spine, and sacrum)",
        isCommon: true
      },
      {
        name: "Manual Osteopathy",
        description: "Advanced clinical skills for osteopathic assessment and treatment",
        isCommon: false
      }
    ]
  },
  asian_eastern_traditions: {
    name: "Asian & Eastern Traditions",
    description: "Traditional massage techniques from Asian cultures based on energy meridians and holistic principles",
    types: [
      {
        name: "Thai Massage",
        description: "Combines stretching, acupressure, and yoga-like movements on a floor mat",
        isCommon: true
      },
      {
        name: "Shiatsu",
        description: "Japanese technique using finger/thumb pressure on meridian points",
        isCommon: true
      },
      {
        name: "Reflexology",
        description: "Pressure on specific reflex points on feet, hands, and ears",
        isCommon: true
      },
      {
        name: "Acupressure",
        description: "Pressure applied to specific points to restore energy flow",
        isCommon: false
      },
      {
        name: "Traditional Chinese Massage (Tui Na)",
        description: "Chinese therapeutic bodywork focusing on meridians and acupoints",
        isCommon: false
      },
      {
        name: "Lomi Lomi",
        description: "Hawaiian massage with flowing, rhythmic strokes",
        isCommon: false
      },
      {
        name: "Ayurvedic Massage",
        description: "Traditional Indian massage techniques based on dosha types",
        isCommon: false
      }
    ]
  },
  advanced_specialized_modalities: {
    name: "Advanced Specialized Modalities",
    description: "Contemporary evidence-based techniques and specialized treatment methods",
    types: [
      {
        name: "Instrument Assisted Soft Tissue Release (IASTM)",
        description: "Uses specialized tools for soft tissue mobilization and scar tissue treatment",
        isCommon: false
      },
      {
        name: "Ligamentous Articular Strain Techniques (LAST)",
        description: "Addresses ligament and joint dysfunctions",
        isCommon: false
      },
      {
        name: "Dermoneuromodulation",
        description: "Focuses on cutaneous nerves near the skin surface",
        isCommon: false
      },
      {
        name: "Muscle Energy Techniques",
        description: "Gentle joint manipulation to restore healthy motion",
        isCommon: false
      },
      {
        name: "PNF Stretching",
        description: "Proprioceptive neuromuscular facilitation for flexibility and strength",
        isCommon: false
      },
      {
        name: "Active Release Technique",
        description: "Movement-based massage to treat soft tissue adhesions",
        isCommon: false
      },
      {
        name: "Cupping Therapy",
        description: "Uses suction cups on skin to increase blood flow and release tension",
        isCommon: true
      },
      {
        name: "Ashiatsu",
        description: "Massage performed using the therapist's feet for deep, broad pressure",
        isCommon: false
      }
    ]
  },
  relaxation_spa_treatments: {
    name: "Relaxation & Spa Treatments",
    description: "Wellness-focused massage for relaxation, stress relief, and pampering experiences",
    types: [
      {
        name: "Aromatherapy Massage",
        description: "Incorporates essential oils for therapeutic and relaxation benefits",
        isCommon: true
      },
      {
        name: "Chair Massage",
        description: "Seated massage without oils, typically for neck, shoulders, and back",
        isCommon: true
      },
      {
        name: "Indian Head Massage",
        description: "Focuses on scalp, head, neck, and shoulders to reduce tension",
        isCommon: false
      },
      {
        name: "Jade Stone Massage",
        description: "Uses jade stones for their cooling and healing properties",
        isCommon: false
      },
      {
        name: "Four-Handed Massage",
        description: "Two therapists working simultaneously for synchronized treatment",
        isCommon: false
      },
      {
        name: "Couples Massage",
        description: "Partners receiving massage together in the same room",
        isCommon: false
      },
      {
        name: "Raindrop Therapy",
        description: "Essential oils applied along the spine using specific techniques",
        isCommon: false
      }
    ]
  },
  complementary_modalities: {
    name: "Complementary Modalities",
    description: "Alternative healing techniques that may be integrated with massage therapy",
    types: [
      {
        name: "Reiki",
        description: "Japanese energy healing technique using light touch or hands hovering",
        isCommon: false
      },
      {
        name: "Hydrotherapy",
        description: "Water-based treatments including hot/cold applications",
        isCommon: false
      },
      {
        name: "Meditation/Guided Imagery",
        description: "Mental relaxation techniques incorporated into treatment",
        isCommon: false
      },
      {
        name: "Bowen Technique",
        description: "Gentle tissue mobilization using rolling movements",
        isCommon: false
      },
      {
        name: "Alexander Technique",
        description: "Posture and movement re-education",
        isCommon: false
      },
      {
        name: "Ear Candling",
        description: "Alternative therapy using hollow candles (controversial)",
        isCommon: false
      }
    ]
  },
  body_area_specific: {
    name: "Body Area Specific",
    description: "Massage focused on particular body regions or areas",
    types: [
      {
        name: "Foot Massage/Reflexology",
        description: "Focused massage of feet and reflex points",
        isCommon: false
      },
      {
        name: "Hand Massage",
        description: "Massage of hands, wrists, and forearms",
        isCommon: false
      },
      {
        name: "Back Massage",
        description: "Focused treatment of back muscles and spine",
        isCommon: false
      },
      {
        name: "Neck and Shoulder Massage",
        description: "Targets upper body tension and stress",
        isCommon: false
      },
      {
        name: "Head/Scalp Massage",
        description: "Massage of head, scalp, and temples",
        isCommon: false
      },
      {
        name: "Facial Massage",
        description: "Massage of facial muscles for relaxation and skin health",
        isCommon: false
      }
    ]
  },
  specialized_populations: {
    name: "Specialized Populations",
    description: "Massage services adapted for specific populations or conditions",
    types: [
      {
        name: "Geriatric Massage",
        description: "Gentle massage adapted for elderly clients",
        isCommon: false
      },
      {
        name: "Pediatric Massage",
        description: "Massage techniques adapted for children",
        isCommon: false
      },
      {
        name: "Post-Surgical Massage",
        description: "Gentle massage to aid recovery after surgery",
        isCommon: false
      },
      {
        name: "Fibromyalgia Massage",
        description: "Specialized approach for chronic pain conditions",
        isCommon: false
      },
      {
        name: "Carpal Tunnel Treatment",
        description: "Focused treatment for repetitive strain injuries",
        isCommon: false
      }
    ]
  },
  animal_massage: {
    name: "Animal Massage",
    description: "Massage therapy adapted for animals",
    types: [
      {
        name: "Canine Massage",
        description: "Massage techniques for dogs to improve health and fitness",
        isCommon: false
      },
      {
        name: "Equine Massage",
        description: "Massage for horses (common in veterinary settings)",
        isCommon: false
      }
    ]
  }
};

// Helper function to get all services as a flat array
export const getAllServices = () => {
  const services = [];
  Object.keys(massageCategories).forEach(categoryKey => {
    const category = massageCategories[categoryKey];
    category.types.forEach(service => {
      services.push({
        ...service,
        category: category.name,
        categoryKey: categoryKey
      });
    });
  });
  return services;
};

// Helper function to get services sorted by popularity (common services first)
export const getServicesSortedByPopularity = () => {
  const services = getAllServices();
  return services.sort((a, b) => {
    if (a.isCommon && !b.isCommon) return -1;
    if (!a.isCommon && b.isCommon) return 1;
    return 0;
  });
};

// Helper function to search services
export const searchServices = (query) => {
  if (!query || query.trim() === '') {
    return getServicesSortedByPopularity();
  }

  const searchTerm = query.toLowerCase().trim();
  const services = getAllServices();

  return services.filter(service => {
    const nameMatch = service.name.toLowerCase().includes(searchTerm);
    const descriptionMatch = service.description.toLowerCase().includes(searchTerm);
    const categoryMatch = service.category.toLowerCase().includes(searchTerm);

    return nameMatch || descriptionMatch || categoryMatch;
  }).sort((a, b) => {
    // Prioritize common services in search results too
    if (a.isCommon && !b.isCommon) return -1;
    if (!a.isCommon && b.isCommon) return 1;
    return 0;
  });
};
